import { Controller } from "@hotwired/stimulus"

/**
 * MT Method Controller
 * Sets field values based on Wet - Black vs Wet - Fluorescent MT (UV-A) selection
 *
 * For "Wet - Black":
 *   - Contrast Colour: "White" (or keep current value)
 *   - Contrast Paint: "Pfinder 280" (or keep current value)
 *   - Contrast Paint batch no.: keep current value
 *   - Magnetic Particle Batch no.: "1234567890" (if empty)
 *   - Magnetic Particle Type: "Pfinder 251"
 *   - Lighting Level: "1000 lux (white light)"
 *
 * For "Wet - Fluorescent MT (UV-A)":
 *   - Contrast Colour: "N/A"
 *   - Contrast Paint: "N/A"
 *   - Contrast Paint batch no.: "N/A"
 *   - Magnetic Particle Batch no.: "1234567890" (if empty)
 *   - Magnetic Particle Type: "Pfinder 150"
 *   - Lighting Level: "1200 μW/cm² / Visible <20 lux"
 */
export default class extends Controller {
  static targets = [
    "selector",               // Method dropdown (Wet - Black / Wet - Fluorescent MT)
    "magneticParticleType",   // Magnetic Particle Type field
    "lightingLevel",          // Lighting Level field
    "contrastColour",         // Contrast Colour field
    "contrastPaint",          // Contrast Paint field
    "contrastPaintBatch",     // Contrast Paint batch no. field
    "magneticParticleBatch"   // Magnetic Particle Batch no. field
  ]

  connect() {
    console.log("MT Method controller connected")
    // Initialize field values on page load
    this.toggleFields()
  }

  toggleFields() {
    if (!this.hasSelectorTarget) {
      console.log("MT Method selector not found")
      return
    }

    const selectedValue = this.selectorTarget.value
    console.log("MT Method selection:", selectedValue)

    const isWetBlack = selectedValue === 'Wet - Black'
    const isFluorescent = selectedValue === 'Wet - Fluorescent MT (UV-A)'

    // Update Contrast Colour based on method
    if (this.hasContrastColourTarget) {
      if (isFluorescent) {
        this.setFieldValue(this.contrastColourTarget, 'N/A')
      } else if (isWetBlack) {
        // Only set default if currently N/A or empty
        const currentValue = this.getFieldValue(this.contrastColourTarget)
        if (currentValue === 'N/A' || currentValue === '') {
          this.setFieldValue(this.contrastColourTarget, 'White')
        }
      }
    }

    // Update Contrast Paint based on method
    if (this.hasContrastPaintTarget) {
      if (isFluorescent) {
        this.setFieldValue(this.contrastPaintTarget, 'N/A')
      } else if (isWetBlack) {
        // Only set default if currently N/A or empty
        const currentValue = this.getFieldValue(this.contrastPaintTarget)
        if (currentValue === 'N/A' || currentValue === '') {
          this.setFieldValue(this.contrastPaintTarget, 'Pfinder 280')
        }
      }
    }

    // Update Contrast Paint batch no. based on method
    if (this.hasContrastPaintBatchTarget) {
      if (isFluorescent) {
        this.setFieldValue(this.contrastPaintBatchTarget, 'N/A')
      }
      // For Wet - Black, keep the current value (don't reset)
    }

    // Update Magnetic Particle Batch no. based on method
    if (this.hasMagneticParticleBatchTarget) {
      const currentValue = this.getFieldValue(this.magneticParticleBatchTarget)
      // Set default value if empty or needs default
      if (currentValue === '' || currentValue === null || currentValue === undefined) {
        this.setFieldValue(this.magneticParticleBatchTarget, '1234567890')
      }
    }

    // Update Magnetic Particle Type based on method
    if (this.hasMagneticParticleTypeTarget) {
      if (isWetBlack) {
        this.setFieldValue(this.magneticParticleTypeTarget, 'Pfinder 251')
      } else if (isFluorescent) {
        this.setFieldValue(this.magneticParticleTypeTarget, 'Pfinder 150')
      }
    }

    // Update Lighting Level based on method
    if (this.hasLightingLevelTarget) {
      if (isWetBlack) {
        this.setFieldValue(this.lightingLevelTarget, '1000 lux (white light)')
      } else if (isFluorescent) {
        this.setFieldValue(this.lightingLevelTarget, '1200 μW/cm² / Visible <20 lux')
      }
    }

    // Log state for debugging
    console.log(`MT Method: ${selectedValue}, Fluorescent=${isFluorescent}, WetBlack=${isWetBlack}`)
  }

  /**
   * Helper to get field value, handling both regular inputs and Tom Select dropdowns
   */
  getFieldValue(input) {
    if (!input) return ''

    if (input.tomselect) {
      return input.tomselect.getValue()
    } else {
      return input.value
    }
  }

  /**
   * Helper to set field value, handling both regular inputs and Tom Select dropdowns
   */
  setFieldValue(input, value) {
    if (!input) return

    if (input.tomselect) {
      input.tomselect.setValue(value)
    } else {
      input.value = value
    }
  }
}
